import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Faux Pas Detection — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/faux-pas-detection.md (Stone-Baron-Cohen 1998).
 *   E1 : tier 1 faux pas (bourde flagrante) — initiation au format.
 *   E2 : tier 2 faux pas (subtil) — calibration baseline.
 *   E3 : contrôle (pas de bourde) — apprentissage du critère « pas de fausse alarme ».
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { itemsByDifficulty } from './corpus';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'faux_pas_detection.demo.step1_title',
        hintKey: 'faux_pas_detection.demo.step1_hint',
        difficulty: 1,
        pickFauxPas: true,
    },
    {
        idx: 2,
        titleKey: 'faux_pas_detection.demo.step2_title',
        hintKey: 'faux_pas_detection.demo.step2_hint',
        difficulty: 2,
        pickFauxPas: true,
    },
    {
        idx: 3,
        titleKey: 'faux_pas_detection.demo.step3_title',
        hintKey: 'faux_pas_detection.demo.step3_hint',
        difficulty: 1,
        pickFauxPas: false,
    },
];
const STAGE_WIDTH = 320;
function pickDemoItem(difficulty, hasFauxPas) {
    const pool = itemsByDifficulty(difficulty).filter((i) => i.hasFauxPas === hasFauxPas);
    if (pool.length > 0)
        return pool[0];
    // Fallback : n'importe quel item du tier.
    return itemsByDifficulty(difficulty)[0];
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('awaiting_yesno');
    const [pickedYesNo, setPickedYesNo] = useState(null);
    const step = DEMO_STEPS[stepIdx];
    const item = pickDemoItem(step.difficulty, step.pickFauxPas);
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const handlePick = (picked) => {
        if (phase !== 'awaiting_yesno')
            return;
        setPickedYesNo(picked);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('awaiting_yesno');
        setPickedYesNo(null);
    };
    const expectedYes = item.hasFauxPas;
    const correct = (pickedYesNo === 'yes' && expectedYes) || (pickedYesNo === 'no' && !expectedYes);
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('faux_pas_detection.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("article", { className: "border border-cyan-border-faint bg-cyan-surface p-s4 text-start", style: { width: STAGE_WIDTH }, children: _jsx("p", { className: "text-[13px] leading-6 text-cyan-text", children: item.story }) }), _jsxs("div", { className: "flex w-full flex-col gap-s3", style: { maxWidth: STAGE_WIDTH }, children: [_jsx("button", { type: "button", onClick: () => handlePick('yes'), disabled: phase === 'feedback', className: [
                            'px-s3 py-s2 text-[13.5px] font-medium transition duration-feedback ease-soft-wind active:scale-95 disabled:cursor-not-allowed',
                            phase === 'feedback' && pickedYesNo === 'yes'
                                ? expectedYes
                                    ? 'bg-cyan-core text-cyan-surface'
                                    : 'bg-cyan-media text-cyan-surface'
                                : 'border border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-border-faint',
                        ].join(' '), children: t('games:faux_pas_detection.buttons.yes') }), _jsx("button", { type: "button", onClick: () => handlePick('no'), disabled: phase === 'feedback', className: [
                            'px-s3 py-s2 text-[13.5px] font-medium transition duration-feedback ease-soft-wind active:scale-95 disabled:cursor-not-allowed',
                            phase === 'feedback' && pickedYesNo === 'no'
                                ? !expectedYes
                                    ? 'bg-cyan-core text-cyan-surface'
                                    : 'bg-cyan-media text-cyan-surface'
                                : 'border border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-border-faint',
                        ].join(' '), children: t('games:faux_pas_detection.buttons.no') })] }), phase === 'feedback' && (_jsxs("p", { className: [
                    'max-w-[320px] text-[12.5px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: [correct
                        ? t('games:faux_pas_detection.feedback.correct')
                        : t('games:faux_pas_detection.feedback.incorrect'), item.hasFauxPas && item.whyCorrectIdx !== null && (_jsxs(_Fragment, { children: [' · ', _jsx("span", { className: "text-cyan-text-secondary", children: t('games:faux_pas_detection.feedback.educative_reveal_template', {
                                    actor: item.whoCommitted ?? '',
                                    context: item.whyChoices[item.whyCorrectIdx] ?? '',
                                }) })] }))] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map